// Fetch pricing for a single property (or for all properties). Falls
// back to the default seeded table generated from `hotelDetails.js` when
// the brand team has not yet curated rows for a given property.

import { useEffect, useMemo, useState } from "react";
import { HOTELS } from "@/lib/data";
import { buildDefaultPricingForHotel } from "@/lib/pricingDefaults";

const API = process.env.REACT_APP_BACKEND_URL;

// Build a map { hotel_slug -> rows[] } from the public /api/pricing
// list, merged with frontend defaults for any property the brand team
// hasn't curated yet.
export const usePricingForAllProperties = () => {
  const [overrides, setOverrides] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/pricing`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const map = {};
        for (const item of data.items || []) {
          if (item.hotel_slug) map[item.hotel_slug] = item.rows || [];
        }
        setOverrides(map);
      })
      .catch(() => {
        if (cancelled) return;
        setOverrides({});
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const byHotel = useMemo(() => {
    const map = {};
    for (const h of HOTELS) {
      if (h.coming_soon) continue;
      const override = overrides ? overrides[h.slug] : null;
      map[h.slug] = (override && override.length) ? override : buildDefaultPricingForHotel(h);
    }
    return map;
  }, [overrides]);

  return { byHotel, loading };
};
